import { useI18nContext } from '@/i18n/i18n-react';
import React, { useCallback, useRef, useState, DragEvent } from 'react';

export interface DropzoneProps {
  accept?: string; // same as input.accept, e.g. "image/*"
  multiple?: boolean;
  maxSizeBytes?: number; // optional limit for file size
  className?: string; // wrapper classes
  children?: React.ReactNode; // custom inner content (keeps default UI if omitted)
  onFiles?: (files: File[]) => Promise<void> | void; // called when files are selected or dropped
  disabled?: boolean;
}

export default function Dropzone({
  accept = '*/*',
  multiple = false,
  maxSizeBytes,
  className = '',
  children,
  onFiles,
  disabled = false,
}: DropzoneProps) {
  const { LL } = useI18nContext();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validateFiles = useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return [] as File[];
      const arr = Array.from(files);
      if (!multiple && arr.length > 1) arr.splice(1);
      if (maxSizeBytes) {
        const filtered = arr.filter((f) => f.size <= maxSizeBytes);
        if (filtered.length !== arr.length)
          setError(
            `Um ou mais arquivos excedem ${Math.round(maxSizeBytes / 1024)} KB`
          );
        return filtered;
      }
      setError(null);
      return arr;
    },
    [maxSizeBytes, multiple]
  );

  const handleFiles = useCallback(
    async (files: FileList | null) => {
      const validated = validateFiles(files);
      if (validated.length === 0) return;
      try {
        await onFiles?.(validated);
      } catch (err) {
        console.error('Dropzone onFiles error', err);
      }
    },
    [onFiles, validateFiles]
  );

  const onInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleFiles(e.target.files);
      // reset input so same file can be selected again if needed
      e.currentTarget.value = '';
    },
    [handleFiles]
  );

  // drag handlers
  const onDragOver = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    setIsDragOver(true);
  };
  const onDragLeave = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };
  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    setIsDragOver(false);
    const dtFiles = e.dataTransfer?.files ?? null;
    handleFiles(dtFiles);
  };

  return (
    <div className={`relative ${className}`}>
      {/* hidden native input */}
      <input
        ref={inputRef}
        type="file"
        aria-label={LL.common.selectTheFile()}
        accept={accept}
        multiple={multiple}
        className="hidden"
        onChange={onInputChange}
        aria-hidden={disabled}
        disabled={disabled}
      />

      {/* visible drop area */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => !disabled && inputRef.current?.click()}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && !disabled)
            inputRef.current?.click();
        }}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        className={`flex items-center justify-center p-3 rounded-lg border transition cursor-pointer select-none outline-none
          ${isDragOver ? 'border-dashed border-gray bg-input' : 'border-input bg-transparent'}
          ${disabled ? 'opacity-50 pointer-events-none' : ''}`}
        aria-disabled={disabled}
      >
        {children ?? (
          <div className="flex flex-col items-center text-center">
            <div className="font-regular text-sm">
              {LL.common.selectTheFile()}
            </div>

            {error && <div className="text-xs text-red-500 mt-2">{error}</div>}
          </div>
        )}
      </div>
    </div>
  );
}
